import React, { useState, useCallback, useEffect, useRef } from 'react';
import { IoClose } from 'react-icons/io5';
import '../styles/VoiceCall.css';
import { useConversation } from '@11labs/react';

interface VoiceCallOverlayProps {
  onClose: () => void;
  visible: boolean;
}

const VoiceCallOverlay: React.FC<VoiceCallOverlayProps> = ({ onClose, visible }) => {
  const [callState, setCallState] = useState<'idle' | 'connecting' | 'active' | 'ended'>('idle');
  const audioStreamRef = useRef<MediaStream | null>(null);

  // Use the official Eleven Labs conversation hook
  const conversation = useConversation({
    onConnect: () => {
      console.log('Connected to Eleven Labs');
      setCallState('active');
    },
    onDisconnect: () => {
      console.log('Disconnected from Eleven Labs');
      if (callState === 'active') {
        setCallState('ended');
      }
    },
    onMessage: (message) => {
      console.log('Message from Eleven Labs:', message);
    },
    onError: (error) => {
      console.error('Eleven Labs error:', error);
      setCallState('ended');
    },
  });

  // Initialize call when the overlay becomes visible
  useEffect(() => {
    if (visible && callState === 'idle') {
      startCall();
    }
  }, [visible]);

  // Clean up when component unmounts
  useEffect(() => {
    return () => {
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(track => track.stop());
        audioStreamRef.current = null;
      }
    };
  }, []);

  const startCall = useCallback(async () => {
    setCallState('connecting');
    
    try {
      // Request microphone permission
      const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = audioStream;
      
      // Start the conversation with your agent - use type assertion
      await conversation.startSession({
        agentId: 'nodGPSIsYdxMXAvNF3gm',
        apiKey: 'sk_44f929860145547d549d467181c91219e2630aab403b07d0'
      } as any);
      
      console.log('Started conversation session');
    } catch (error) {
      console.error('Failed to start conversation:', error);
      setCallState('idle');
    }
  }, [conversation]);

  const endCall = useCallback(async () => {
    try {
      await conversation.endSession();
      
      // Stop microphone access
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(track => track.stop());
        audioStreamRef.current = null;
      }
    } catch (error) {
      console.error('Error ending conversation:', error);
    }
    
    setCallState('ended');
    
    // Reset and close overlay after a moment
    setTimeout(() => {
      setCallState('idle');
      onClose();
    }, 3000);
  }, [conversation, onClose]);

  if (!visible) return null;

  return (
    <div className="voice-call-overlay">
      <div className="voice-call-container">
        {/* Call Header */}
        <div className="voice-call-header">
          <h3>AI Voice Assistant</h3>
          <p>Ask about Canada's Innovation Strategy</p>
        </div>

        {/* Call Interface */}
        <div className="voice-call-body">
          {/* Logo and animation */}
          <div className="voice-call-avatar">
            <div className="voice-call-avatar-image">
              {callState === 'active' && (
                <div className="voice-call-rings">
                  <div className="ring ring-1"></div>
                  <div className="ring ring-2"></div>
                  <div className="ring ring-3"></div>
                </div>
              )}
            </div>
            
            <p className="voice-call-assistant-label">Canada Innovation AI</p>
            
            {/* Call Status */}
            <div className={`voice-call-status ${callState}`}>
              {callState === 'idle' ? 'Ready to assist you' :
               callState === 'connecting' ? 'Connecting...' :
               callState === 'active' ? (conversation.isSpeaking ? 'Speaking...' : 'Listening...') :
               'Call ended'}
            </div>
          </div>

          {/* Voice animation when call is active */}
          {callState === 'active' && (
            <div className="voice-call-animation">
              {[...Array(11)].map((_, i) => (
                <div
                  key={i}
                  className={`voice-wave ${conversation.isSpeaking ? 'speaking' : 'listening'}`}
                  style={{ 
                    animationDelay: `${i * 60}ms`
                  }}
                ></div>
              ))}
            </div>
          )}

          {/* Call Controls */}
          <div className="voice-call-controls">
            <button 
              className="voice-call-end-button"
              onClick={endCall}
              aria-label="End call"
            >
              <IoClose />
            </button>
          </div>

          {/* Call instructions */}
          <div className="voice-call-instructions">
            {callState === 'connecting' && 
              <p>Connecting to voice service...</p>
            }
            {callState === 'active' && 
              <p>Speak naturally about Canada's innovation strategy or Budget 2024 initiatives</p>
            }
            {callState === 'ended' && 
              <p>Call ended. Thank you for using our AI voice assistant!</p>
            }
          </div>
        </div>
      </div>
    </div>
  );
};

export default VoiceCallOverlay; 